// Navigation for the public Resident Service 360 website: header menus, footer links and where each action goes.

/** Real app flows the website hands over to. */
export const appLinks = {
  getQuotes: '/home/jobs',
  findJobs: '/commercial/jobs',
  createAccount: '/sign-up',
  addCompany: '/sign-up?workspace=supplier',
  housingProviderStart: '/sign-up?next=/housing-provider/repairs',
  signIn: '/sign-in',
  account: '/account',
  resetPassword: '/reset-password',
};

export const KITCHEN_TAP_PHOTO = {
  id: '1774716925718-82ea2f2eb01b',
  ixid: 'M3w4NDM0ODN8MHwxfHJhbmRvbXx8fHx8fHx8fDE3OTEyNTI2OTF8',
  alt: 'A kitchen mixer tap over a sink',
};

export const TERRACE_PHOTO = {
  id: '1588846367931-6fd391c119d8',
  ixid: 'M3w4NDM0ODN8MHwxfHJhbmRvbXx8fHx8fHx8fDE3OTEyNTE0MTV8',
  alt: 'A street of brick terraced houses',
};

/** An Unsplash image URL at a given width. Pages crop it with object-fit, centred, as the boards do. */
export function unsplash(photo, width) {
  return `https://images.unsplash.com/photo-${photo.id}?cs=tinysrgb&fit=max&fm=jpg&ixid=${photo.ixid}&ixlib=rb-4.1.0&q=80&w=${width}`;
}

/** The header dropdown menus. Each item links to the page section that explains it. */
export const menus = [
  {
    id: 'home-services',
    label: 'Home & Services',
    paths: ['/home-services'],
    items: [
      { icon: 'camera', title: 'Report a Repair', line: 'Take a photo. Get it fixed.', to: '/home-services' },
      { icon: 'map-pin', title: 'Find Local Trades', line: 'Checked trades near you.', to: '/home-services#find-local-trades' },
      { icon: 'calendar-clock', title: 'Quotes & Appointments', line: 'Compare prices. Book a time.', to: '/home-services#how-it-works' },
      { icon: 'message-circle', title: 'Messages', line: 'Talk to your tradesperson.', to: '/home-services#track-your-repairs' },
      { icon: 'history', title: 'Service History', line: 'Every job for your home.', to: '/home-services#track-your-repairs' },
      { icon: 'badge-check', title: 'Certification Services', line: 'Gas, electrical and fire safety checks.', to: '/home-services#compliance-services' },
    ],
    feature: { kind: 'photo', title: 'Book a Repair', text: 'Take a photo. Get quotes from checked local tradespeople.', action: 'Get quotes', to: appLinks.getQuotes },
  },
  {
    id: 'for-businesses',
    label: 'For Businesses',
    paths: ['/for-businesses'],
    group: 'For Trades & Services',
    items: [
      { icon: 'map-pin', title: 'Available Jobs', line: 'Repair jobs near you.', to: '/for-businesses#for-trades-and-services' },
      { icon: 'inbox', title: 'Leads / Requests', line: 'Requests sent to you.', to: '/for-businesses#requests-quotes-and-won-work' },
      { icon: 'receipt', title: 'Quotes', line: 'Send a price and a time.', to: '/for-businesses#requests-quotes-and-won-work' },
      { icon: 'calendar-days', title: 'Schedule', line: 'Your booked jobs by day.', to: '/for-businesses#requests-quotes-and-won-work' },
      { icon: 'banknote', title: 'Invoices / Payments', line: 'Invoices and payments.', to: '/for-businesses' },
      { icon: 'badge-check', title: 'Business Profile', line: 'Your checks, area and work.', to: '/for-businesses' },
    ],
    feature: { kind: 'company', title: 'Add Your Company', text: 'Find repair jobs near you.', check: 'No commission on jobs', action: 'Add your company', to: appLinks.addCompany },
  },
  {
    id: 'for-housing-providers',
    label: 'For Housing Providers',
    paths: ['/for-housing-providers'],
    items: [
      { icon: 'inbox', title: 'Repair Requests', line: 'Repairs reported with a photo.', to: '/for-housing-providers#repair-requests' },
      { icon: 'calendar-check', title: 'Planned Jobs & Safety Checks', line: 'Checks for your homes.', to: '/for-housing-providers' },
      { icon: 'users', title: 'Your Trades', line: 'The trades you use.', to: '/for-housing-providers' },
      { icon: 'receipt', title: 'Approvals & Costs', line: 'Quotes and costs.', to: '/for-housing-providers#repair-requests' },
      { icon: 'history', title: 'Property History', line: 'Every job for every home.', to: '/for-housing-providers' },
      { icon: 'message-circle', title: 'Resident Updates', line: 'Residents track their repair.', to: '/for-housing-providers#repair-requests' },
      { icon: 'refresh-cw', title: 'BedMaps Sync', line: 'Connects with BedMaps.', to: '/for-housing-providers#for-bedmaps-providers' },
    ],
    feature: { kind: 'bedmaps', title: 'Works with BedMaps', text: 'BedMaps providers get repair requests from residents straight into BedMaps.', action: 'Get started', to: appLinks.housingProviderStart },
  },
];

export const selfReferLink = { label: 'Self Referral', to: '/enquire' };

export const howItWorksLink = { label: 'How it works', to: '/how-it-works' };

/** Footer link columns (desktop). The phone footer shows the same links in two columns without headings. */
export const footerColumns = [
  { heading: 'For you', links: [selfReferLink, { label: 'Home & Services', to: '/home-services' }] },
  { heading: 'For organisations', links: [{ label: 'For Businesses', to: '/for-businesses' }, { label: 'For Housing Providers', to: '/for-housing-providers' }] },
  { heading: 'Help', links: [{ label: 'How it works', to: '/how-it-works' }, { label: 'Support', to: '/support' }, { label: 'Sign in', to: '/sign-in' }] },
];

export const footerPhoneColumns = [
  [selfReferLink, { label: 'Home & Services', to: '/home-services' }, { label: 'How it works', to: '/how-it-works' }],
  [{ label: 'For Businesses', to: '/for-businesses' }, { label: 'For Housing Providers', to: '/for-housing-providers' }, { label: 'Support', to: '/support' }, { label: 'Sign in', to: '/sign-in' }],
];

export const legalLinks = [{ label: 'Privacy', to: '/privacy' }, { label: 'Terms', to: '/terms' }];

/** The menu whose pages include this path, if any. */
export function menuForPath(pathname) {
  return menus.find((menu) => menu.paths.some((path) => pathname === path || pathname.startsWith(`${path}/`)))?.id || null;
}
